import React, { useState, useRef, useEffect } from "react";
import ChevronDownIcon from "../icons/chevronDown.icon";
import { useTranslation } from "../core/translation.core";

interface Option {
  label: string;
  value: string;
}

function getCountFromLabel(label: string): number {
  const match = label.match(/\((\d+)\)$/);
  return match ? parseInt(match[1], 10) : 0;
}

interface FilterSectionWithDropdownProps {
  title: string;
  attributeId: string; // Add attributeId for API integration
  options?: Option[];
  onOptionSelect?: (attributeId: string, options: string[]) => void;
  selectedValues?: string[];
  expanded?: boolean;
}

export default function FilterSectionWithDropdown({
  title,
  attributeId,
  options = [],
  onOptionSelect = () => {},
  selectedValues = [],
  expanded = false,
}: FilterSectionWithDropdownProps): JSX.Element {
  const { t, msg } = useTranslation();
  const [isDropdownOpen, setIsDropdownOpen] = useState<boolean>(false);
  const [searchValue, setSearchValue] = useState<string>("");
  const [filteredOptions, setFilteredOptions] = useState<Option[]>(options);
  const [seenOptions, setSeenOptions] = useState<Option[]>(options);
  const componentRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (componentRef.current && !componentRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    setSeenOptions((prev) => {
      const merged = new Map(prev.map((option) => [option.value, option]));
      options.forEach((option) => {
        merged.set(option.value, option);
      });
      return Array.from(merged.values());
    });
  }, [options]);

  useEffect(() => {
    // Filter options based on search text only, DON'T exclude already selected options
    const filtered = seenOptions.filter((option) => option.label.toLowerCase().includes(searchValue.toLowerCase()));
    setFilteredOptions(filtered);
  }, [searchValue, seenOptions]);

  // Force the dropdown to open when component mounts if there are selected values
  useEffect(() => {
    if (selectedValues.length > 0 && !isDropdownOpen) {
      setIsDropdownOpen(true);
    }
    // eslint-disable-next-line
  }, []);

  function handleInputChange(event: React.ChangeEvent<HTMLInputElement>): void {
    setSearchValue(event.target.value);
  }

  function toggleDropdown(event: React.MouseEvent): void {
    // Prevent event propagation
    event.stopPropagation();
    event.preventDefault();

    // If we have selected values and are about to close, keep it open
    if (isDropdownOpen && selectedValues.length > 0) {
      // Don't close if we have selections, unless explicitly clicking the close button
      const target = event.target as HTMLElement;
      if (target.tagName === "svg" || target.tagName === "path") {
        setIsDropdownOpen(!isDropdownOpen);
      }
    } else {
      setIsDropdownOpen(!isDropdownOpen);
    }

    if (!isDropdownOpen) {
      setSearchValue("");
    }
  }

  return (
    <div ref={componentRef} className="relative bg-neutral-100 rounded-lg">
      <div className="px-3 sm:px-4 py-4 sm:py-5">
        {/* Header with title and dropdown toggle */}
        <div className="flex items-center justify-between mb-3 sm:mb-4">
          <span className="text-base sm:text-lg font-medium text-gray-900">{title}</span>
          {!expanded && (
            <button
              onClick={toggleDropdown}
              className="focus:outline-none"
              type="button"
              aria-label={isDropdownOpen ? "Close dropdown" : "Open dropdown"}
            >
              <ChevronDownIcon
                className={`w-4 sm:w-5 h-4 sm:h-5 text-gray-500 transform transition-transform ${
                  isDropdownOpen ? "rotate-180" : ""
                }`}
              />
            </button>
          )}
        </div>

        {/* Search input */}
        <input
          type="text"
          value={searchValue}
          onChange={handleInputChange}
          placeholder={t(msg.product.filterSearchPlaceholder).replace("{title}", title)}
          className="w-full bg-transparent border-b border-gray-200 pb-2 focus:outline-none focus:border-gray-400 text-gray-700 placeholder-gray-400 text-sm sm:text-base"
          onClick={(e) => e.stopPropagation()} // Prevent closing when clicking the input
        />
      </div>

      {/* Dropdown options */}
      {(expanded || isDropdownOpen) && (
        <div
          className={`bg-white rounded-lg py-2 max-h-60 overflow-y-auto ${
            expanded ? "relative mt-2" : "absolute z-50 top-full left-0 right-0 mt-1 shadow-lg"
          }`}
          onClick={(e) => e.stopPropagation()} // Prevent dropdown closing when clicking inside options area
        >
          {filteredOptions.length === 0 ? (
            <div className="px-3 sm:px-4 py-2 text-gray-500 text-sm">{t(msg.common.noOptionsFound)}</div>
          ) : (
            [...filteredOptions]
              .filter((option) => getCountFromLabel(option.label) > 0)
              .sort((a, b) => a.label.localeCompare(b.label))
              .map((option) => {
              const isSelected = selectedValues.includes(option.value);
              return (
                <label
                  key={option.value}
                  className="flex items-center px-3 sm:px-4 py-2 hover:bg-gray-50 cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => {
                      const newValues = isSelected
                        ? selectedValues.filter((value) => value !== option.value)
                        : [...selectedValues, option.value];
                      onOptionSelect(attributeId, newValues);
                    }}
                    className="w-4 h-4 border-gray-300 rounded text-red-500 focus:ring-red-500"
                  />
                  <span className={`ml-2 text-sm sm:text-base ${isSelected ? "font-medium text-red-500" : "text-gray-700"}`}>
                    {option.label}
                  </span>
                </label>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}
